// The sidebar menu: its static fallback, its order and route tables, and the builder that turns the
// permission payload into sections. Desktop and mobile both import this, so they cannot drift apart.
import type React from "react";
import IconDashboard from "../assets/images/IconDashboard";
import IconCheckBook from "../assets/images/IconCheckbook";
import IconSetting from "../assets/images/IconSetting";
import IconReport from "../assets/images/IconReport";
import IconCompany from "../assets/images/IconCompany";
import IconInventory from "../assets/images/IconInventory";
import IconReservations from "../assets/images/IconReservations";
import IconConfiguration from "../assets/images/IconConfiguration";
import IconHome from "../assets/images/IconHome";
import IconLedger from "../assets/images/IconLedger";
import IconSalePoints from "../assets/images/IconSalePoints";
import IconItems from "../assets/images/IconItems";
import IconModule from "../assets/images/IconModule";
import IconDevice from "../assets/images/IconDevice";
import IconFiance from "../assets/images/IconFiance";
import IconAdmin from "../assets/images/IconAdmin";

export type ChildMenu = { title: string; path: string };
export type MenuItem = {
  title: string;
  icon: React.FC<{ active?: boolean; size?: number }>;
  path?: string;
  children?: ChildMenu[];
  isActive?: boolean;
};

export const menuSidebarTest: MenuItem[] = [
  {
    title: "Dashboard",
    icon: IconDashboard,
    path: "/dashboard",
    isActive: false,
  },
  {
    title: "Company",
    icon: IconCompany,
    isActive: false,
    children: [
      { title: "Company Details", path: "/companies" },
      { title: "Locations", path: "/locations" },
      { title: "Floor Plans", path: "/floorPlan" },
      { title: "Sale Points", path: "/salePoint" },
      { title: "Reservations Setup", path: "/reservations" },
      { title: "Employees", path: "/employees" },
      { title: "Clients", path: "/clients" },
    ],
  },
  {
    title: "Inventory",
    icon: IconInventory,
    isActive: false,
    children: [
      { title: "Items", path: "/items" },
      { title: "Attributes", path: "/attributes" },
      { title: "Inventory", path: "/inventory" },
      { title: "Product Modifiers", path: "/productModifiers" },
    ],
  },
  {
    title: "Inventory Management",
    icon: IconCheckBook,
    isActive: false,
    children: [
      { title: "Orders", path: "/orders" },
      { title: "Transfers", path: "/transfer" },
      { title: "Deliveries", path: "/deliveries" },
      { title: "Count", path: "/count" },
      { title: "Count Approval", path: "/countsApproval" },
      { title: "Stock Counts", path: "/stockCounts" },
      { title: "Corrections", path: "/corrections" },
      { title: "Order Templates", path: "/orderTemplates" },
      { title: "Order Approval", path: "/orderApproval" },
      { title: "Supplier Inventory", path: "/supplierInventory" },
    ],
  },
  {
    title: "Device Configurations",
    icon: IconDevice,
    isActive: false,
    children: [
      { title: "Devices", path: "/devices" },
      { title: "Device Users", path: "/deviceUser" },
      { title: "Device Tender", path: "/deviceTender" },
      { title: "Device Health", path: "/deviceHealth" },
      { title: "App Version", path: "/appVersion" },
    ],
  },
  {
    title: "Settings",
    icon: IconSetting,
    isActive: false,
    children: [
      { title: "Reasons", path: "/reasons" },
      { title: "Schedules", path: "/schedules" },
      { title: "Promotion", path: "/promotion" },
    ],
  },
  {
    title: "Administration",
    icon: IconAdmin,
    isActive: false,
    children: [
      { title: "Tax", path: "/tax" },
      { title: "Payment Terms", path: "/terms" },
      { title: "Roles", path: "/roles" },
      { title: "Languages", path: "/languages" },
    ],
  },
  {
    title: "Finance",
    icon: IconFiance,
    isActive: false,
    children: [
      { title: "Purchases", path: "/purchases" },
      { title: "Sales", path: "/sales" },
      { title: "Assets", path: "/assets" },
      { title: "Purchase Ledger", path: "/purchaseLedger" },
      { title: "Sales Ledger", path: "/salesLedger" },
      { title: "Transaction Month", path: "/transactionMonth" },
      { title: "Financial Reports", path: "/financialReports" },
    ],
  },
  {
    title: "Report",
    icon: IconReport,
    isActive: false,
    children: [
      { title: "General Report", path: "/generalReport" },
      { title: "Item Movement", path: "/itemMovement" },
      { title: "Recent Sales", path: "/recentSales" },
      { title: "Sales Overview", path: "/reporting/sales-overview" },
      { title: "Product Performance", path: "/productPerformance" },
      { title: "POS Reports", path: "/pos-reports" },
      { title: "Table Reservations", path: "/tableReservations" },
      { title: "Customer Orders", path: "/customerOrders" },
    ],
  },
];

const toCamelCase = (str: string) => {
  return str
    .toLowerCase()
    .split(/[\s-_]+/)
    .map((word, index) => {
      if (index === 0) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join('');
};

const getIconForSection = (sectionName: string) => {
  const iconMap: Record<string, any> = {
    "Company": IconCompany,
    "Inventory": IconInventory,
    "Inventory Management": IconCheckBook,
    "Device Configurations": IconDevice,
    "Settings": IconSetting,
    "Administration": IconAdmin,
    "Finance": IconFiance,
    "Report": IconReport,
    // Every group the backend can emit gets its own icon; anything new falls back
    // to a neutral "module" glyph rather than borrowing the Dashboard squares.
    "Reservations": IconReservations,
    "Reservations Setup": IconReservations,
    "Configuration": IconConfiguration,
    "Home": IconHome,
    "Ledger": IconLedger,
    "Sale Points": IconSalePoints,
    "Items": IconItems,
    "Dashboard": IconDashboard,
  };
  return iconMap[sectionName] || IconModule;
};

const menuOrder = [
  "Dashboard",
  "Company",
  "Inventory",
  "Inventory Management",
  "Device Configurations",
  "Settings",
  "Administration",
  "Finance",
  "Report",
];

const childrenOrder: Record<string, string[]> = {
  "Company": [
    "Company Details",
    "Locations",
    "Floor Plans",
    "Sale Points",
    "Reservations Setup",
    "Employees",
    "Clients",
  ],
  "Inventory": ["Items", "Attributes", "Inventory", "Product Modifiers"],
  // "Orders" and "Count" are the older names of Order Management and Stock Count; both keep their slot.
  "Inventory Management": [
    "Order Management",
    "Orders",
    "Transfers",
    "Deliveries",
    "Stock Count",
    "Count",
    "Count Approval",
    "Corrections",
    "Order Templates",
    "Order Approval",
    "Supplier Inventory",
    "Supplier Return",
  ],
  "Device Configurations": [
    "Devices",
    "Device Users",
    "Device Tender",
    "Device Health",
    "App Version",
  ],
  "Settings": ["Reasons", "Schedules", "Promotion"],
  "Administration": ["Tax", "Payment Terms", "Roles", "Languages"],
  "Finance": [
    "Purchases",
    "Sales",
    "Assets",
    "Purchase Ledger",
    "Sales Ledger",
    "Transaction Month",
  ],
  "Report": [
    "General Report",
    "Item Movement",
    "Recent Sales",
    "Sales Overview",
    "Product Performance",
    "POS Reports",
    "Table Reservations",
    "Customer Orders",
  ],
};

/** A folded link joins a section unless the section already lists that title. */
const withFoldedChild = (children: any[] = [], folded: { title: string }) =>
  children.some((child) => child.title === folded.title) ? children : [...children, folded];

const sortChildren = (children: any[], sectionName: string) => {
  const order = childrenOrder[sectionName];
  if (!order) return children;

  return children.sort((a, b) => {
    const indexA = order.indexOf(a.title);
    const indexB = order.indexOf(b.title);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

const menuTemplate: Record<string, Record<string, string>> = {
  "Company": {
    "Company Details": "/companies",
    "Locations": "/locations",
    "Floor Plans": "/floorPlan",
    "Sale Points": "/salePoint",
    "Reservations Setup": "/reservations",
    "Employees": "/employees",
    "Clients": "/clients",
  },
  "Inventory": {
    "Items": "/items",
    "Attributes": "/attributes",
    "Inventory": "/inventory",
    "Product Modifiers": "/productModifiers",
  },
  "Inventory Management": {
    "Order Management": "/orderManagement",
    "Transfers": "/transfer",
    "Deliveries": "/deliveries",
    "Stock Count": "/count",
    "Count Approval": "/countsApproval",
    "Corrections": "/corrections",
    "Order Templates": "/orderTemplates",
    "Order Approval": "/orderApproval",
    "Supplier Inventory": "/supplierInventory",
    "Supplier Return": "/supplierReturn",
  },
  "Device Configurations": {
    "Devices": "/devices",
    "Device Users": "/deviceUser",
    "Device Tender": "/deviceTender",
    "Device Health": "/deviceHealth",
    "App Version": "/appVersion",
  },
  "Settings": {
    "Reasons": "/reasons",
    "Schedules": "/schedules",
    "Promotion": "/promotion",
  },
  "Administration": {
    "Tax": "/tax",
    "Payment Terms": "/terms",
    "Roles": "/roles",
    "Languages": "/languages",
  },
  "Finance": {
    "Purchases": "/purchases",
    "Sales": "/sales",
    "Assets": "/assets",
    "Purchase Ledger": "/purchaseLedger",
    "Sales Ledger": "/salesLedger",
    "Transaction Month": "/transactionMonth",
  },
  "Report": {
    "General Report": "/generalReport",
    "Item Movement": "/itemMovement",
    "Recent Sales": "/recentSales",
    "Sales Overview": "/reporting/sales-overview",
    "Product Performance": "/productPerformance",
    "POS Reports": "/pos-reports",
    "Table Reservations": "/tableReservations",
    "Customer Orders": "/customerOrders",
  },
};

// Permissions that gate a control on a page, not a destination of their own:
// a menu row for one guesses a path no route serves and lands on the dashboard.
const NON_PAGE_PERMISSIONS = new Set([
  "Quick Add Items",
  "Service Charges",
  "Device Settings",
  "Stock Count Approval",
]);

const isMenuEntry = (item: any) =>
  item.value === true && !NON_PAGE_PERMISSIONS.has(item.name);

export const buildMenuFromBackend = (backendData: any[]): MenuItem[] => {
  const menuMap = new Map<string, any>();
  let foldedReservations: any = null;
  let foldedTableReservations: any = null;

  backendData.forEach((section) => {
    // Xử lý đặc biệt cho Home - chuyển Dashboard thành menu item riêng
    if (section.name === "Home") {
      const dashboardItem = section.value.find((item: any) => item.name === "Dashboard" && item.value === true);
      if (dashboardItem) {
        menuMap.set("Dashboard", {
          title: "Dashboard",
          icon: getIconForSection("Dashboard"),
          path: "/dashboard",
          isActive: false,
          id: dashboardItem.id,
        });
      }
      return; // Bỏ qua phần xử lý tiếp theo cho Home
    }

    // The backend's "Reservations" section is folded away: setup goes under Company, bookings under Report.
    // Bookings is "Table Reservations" since backend migration 1778665000000; "Reservations" is the old name.
    if (section.name === "Reservations") {
      const setups = section.value.find(
        (item: any) => item.name === "Reservation Setups" && item.value === true
      );
      if (setups) {
        foldedReservations = {
          title: "Reservations Setup",
          path: "/reservations",
          id: setups.id,
        };
      }
      const bookings = section.value.find(
        (item: any) =>
          (item.name === "Table Reservations" || item.name === "Reservations") &&
          item.value === true
      );
      if (bookings) {
        foldedTableReservations = {
          title: "Table Reservations",
          path: "/tableReservations",
          id: bookings.id,
        };
      }
      return;
    }

    const sectionTemplate = menuTemplate[section.name];

    if (sectionTemplate) {
      const allowedChildren = section.value
        .filter(isMenuEntry)
        .flatMap((item: any) => {
          const child = {
            title: item.name,
            path: sectionTemplate[item.name] || `/${toCamelCase(item.name)}`,
            id: item.id,
          };
          if (section.name === "Report" && item.name === "Recent Sales") {
            return [
              child,
              {
                title: "Sales Overview",
                path: "/reporting/sales-overview",
                id: item.id,
              },
            ];
          }
          return [child];
        });

      if (allowedChildren.length > 0) {
        menuMap.set(section.name, {
          title: section.name,
          icon: getIconForSection(section.name),
          isActive: false,
          children: sortChildren(allowedChildren, section.name),
          id: section.id,
        });
      }
    }
    else {
      const dynamicChildren = section.value
        .filter(isMenuEntry)
        .map((item: any) => ({
          title: item.name,
          path: item.path || `/${toCamelCase(item.name)}`,
          id: item.id,
        }));

      if (dynamicChildren.length > 0) {
        menuMap.set(section.name, {
          title: section.name,
          icon: getIconForSection(section.name),
          isActive: false,
          children: dynamicChildren,
          id: section.id,
        });
      }
    }
  });

  if (foldedReservations) {
    const company = menuMap.get("Company");
    if (company) {
      const mergedChildren = withFoldedChild(company.children, foldedReservations);
      menuMap.set("Company", {
        ...company,
        children: sortChildren(mergedChildren, "Company"),
      });
    } else {
      menuMap.set("Company", {
        title: "Company",
        icon: getIconForSection("Company"),
        isActive: false,
        children: [foldedReservations],
      });
    }
  }

  if (foldedTableReservations) {
    const report = menuMap.get("Report");
    if (report) {
      const mergedChildren = withFoldedChild(report.children, foldedTableReservations);
      menuMap.set("Report", {
        ...report,
        children: sortChildren(mergedChildren, "Report"),
      });
    } else {
      menuMap.set("Report", {
        title: "Report",
        icon: getIconForSection("Report"),
        isActive: false,
        children: [foldedTableReservations],
      });
    }
  }

  const sortedMenus: MenuItem[] = [];

  menuOrder.forEach((menuName) => {
    if (menuMap.has(menuName)) {
      sortedMenus.push(menuMap.get(menuName));
    }
  });

  menuMap.forEach((menu, key) => {
    if (!menuOrder.includes(key)) {
      sortedMenus.push(menu);
    }
  });

  return sortedMenus;
};
